import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Route mapping for sidebar navigation
const TAB_TO_ROUTE = {
  'dashboard': '/app/dashboard',
  'media': '/app/listings',
  'clients': '/app/clients',
  'appointments': '/app/appointments',
  'calendar': '/app/calendar',
  'tasks': '/app/tasks',
  'team': '/app/team',
  'products': '/app/products',
  'settings': '/app/settings',
  'orderform': '/app/order-form',
  'notifications': '/app/notifications',
  'admin': '/app/admin'
};

const Sidebar = ({ 
  user,
  permissions,
  hasPermission,
  activeTab,
  setActiveTab,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  setShowAppointmentForm,
  openNewAppointmentForm,
  fetchSettings,
  products,
  teamMembers,
  darkMode,
  setDarkMode,
  logout,
  isBlurred = false,
  onCommandCenterOpen,
  isCollapsed = false,
  setIsCollapsed,
  isMasterAdmin = false,
  pendingSubmissionsCount = 0,
  logoUrl = null,
  businessName = null
}) => {
  // Use businessName prop (from accountSettings) if available, otherwise fallback to user.business_name
  const displayBusinessName = businessName || user?.business_name;
  
  const mainTabs = [
    // Master Admin tab - only visible to master admin
    ...(isMasterAdmin ? [{
      key: 'admin',
      label: 'Admin Panel',
      permission: null,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    }] : []),
    { 
      key: 'dashboard', 
      label: 'Dashboard',
      permission: { resource: 'dashboard', action: 'view' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )
    },
    { 
      key: 'calendar', 
      label: 'Calendar',
      permission: { resource: 'appointments', action: 'view' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    { 
      key: 'clients', 
      label: 'Clients',
      permission: { resource: 'clients', action: 'view' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    { 
      key: 'appointments', 
      label: 'Appointments',
      permission: { resource: 'appointments', action: 'view' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      )
    },
    { 
      key: 'media', 
      label: 'Listings',
      permission: { resource: 'listings', action: 'view' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    { 
      key: 'tasks', 
      label: 'Tasks',
      permission: { resource: 'tasks', action: 'view' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      )
    }
  ].filter(tab => !tab.permission || !hasPermission || hasPermission(tab.permission.resource, tab.permission.action));

  const adminTabs = [
    { 
      key: 'team', 
      label: 'Team',
      permission: { resource: 'team', action: 'view' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
    { 
      key: 'products', 
      label: 'Products',
      permission: { resource: 'settings', action: 'view_products' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      )
    },
    { 
      key: 'orderform', 
      label: 'Order Form',
      permission: { resource: 'settings', action: 'view_products' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    { 
      key: 'notifications', 
      label: 'Notifications',
      permission: { resource: 'settings', action: 'view_products' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      )
    },
    {
      key: 'payroll',
      label: 'Payroll',
      permission: { resource: 'settings', action: 'manage_business' },
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    { 
      key: 'settings', 
      label: 'Settings',
      permission: null, // Always show settings (content will be different based on permissions)
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    }
  ].filter(tab => !tab.permission || !hasPermission || hasPermission(tab.permission.resource, tab.permission.action));

  const handleScheduleAppointment = async () => {
    if (products.length === 0 || teamMembers.length === 0) {
      await fetchSettings();
    }
    // Use the new function that properly resets the form
    if (openNewAppointmentForm) {
      openNewAppointmentForm();
    } else {
      setShowAppointmentForm(true);
    }
    setIsMobileMenuOpen(false);
  };

  const navigate = useNavigate();

  const handleTabClick = (tabKey) => {
    const route = TAB_TO_ROUTE[tabKey];
    if (route) {
      navigate(route);
    }
    setActiveTab(tabKey);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={`clara-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''} ${isCollapsed ? 'sidebar-collapsed' : ''} ${isBlurred ? 'filter blur-sm transition-all' : ''}`}>
      <div className="clara-sidebar-header">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img 
                src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} 
                alt={displayBusinessName} 
                className="h-8 w-auto object-contain max-w-[150px] brightness-0 invert" 
              />
            ) : (
              <h1 className="text-lg font-bold text-white" style={{ margin: 0 }}>{displayBusinessName}</h1>
            )}
          </div>
          <button 
            className="hamburger-menu md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            ✕
          </button>
        </div>
      </div>
      
      <div className="clara-sidebar-scrollable">
        {hasPermission && hasPermission('appointments', 'create') && (
          <div className="px-3 py-2">
            <button
              onClick={handleScheduleAppointment}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-3 rounded-lg shadow-md transition-colors flex items-center justify-center gap-2 text-sm"
            >
              Schedule Appointment
            </button>
          </div>
        )}
        
        <nav className="clara-sidebar-nav">
          {mainTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              className={`clara-sidebar-nav-item ${activeTab === tab.key ? 'active' : ''}`}
            >
              <span className="flex items-center space-x-2.5">
                {tab.icon}
                <span>{tab.label}</span>
                {tab.key === 'appointments' && pendingSubmissionsCount > 0 && (
                  <span className="ml-auto bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded min-w-[20px] text-center">
                    {pendingSubmissionsCount}
                  </span>
                )}
              </span>
            </button>
          ))}
          
          <div className="border-t border-gray-600 my-2 mx-4"></div>
          
          {adminTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              className={`clara-sidebar-nav-item ${activeTab === tab.key ? 'active' : ''}`}
            >
              <span className="flex items-center space-x-2.5">
                {tab.icon}
                <span>{tab.label}</span>
              </span>
            </button>
          ))}
        </nav>
        
        <div className="clara-sidebar-footer">
          {/* Command Center Button - Only for admins */}
          {hasPermission && hasPermission('settings', 'manage_business') && (
            <button
              onClick={onCommandCenterOpen}
              className="w-full mb-3 px-3 py-2.5 text-white rounded-lg transition-all flex items-center justify-between group shadow-md hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #1f2937 0%, #9333ea 100%)' }}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="text-xs font-medium">Command Center</span>
              </div>
              <kbd className="px-1.5 py-0.5 text-xs font-semibold text-white bg-black bg-opacity-20 rounded">
                ⌘K
              </kbd>
            </button>
          )}
          
          <div className="mb-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-300">Dark Mode</span>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  darkMode ? 'bg-blue-600' : 'bg-gray-500'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    darkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          
          <div className="text-xs text-gray-300 mb-2">Welcome, {displayBusinessName}</div>
          <button
            onClick={logout}
            className="bg-gray-500 text-white w-full py-1.5 px-3 rounded text-sm hover:bg-gray-400 transition-colors font-normal"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
